import React, { useContext, useState } from 'react';
import { TextField, IconButton, Box, Button, Typography, TableRow, TableCell } from '@mui/material';
import { Plus } from '@phosphor-icons/react';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { ThemeContext } from 'styled-components';

const AddStatementRow = ({ 
    templateId, 
    onSubmit, 
    isOwner 
}) => {
    const theme = useContext(ThemeContext);
    const [cost, setCost] = useState('');
    const [isAdding, setIsAdding] = useState(false);

    const handleStartAdd = () => {
        setIsAdding(true);
        setCost('');
    };

    const handleCancelAdd = () => {
        setIsAdding(false);
        setCost('');
    };

    const handleInputChange = (event) => {
        const value = event.target.value;
        // Allow empty string, numbers, and decimal point
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setCost(value);
        }
    };

    const isValidCost = cost !== '' && parseFloat(cost) > 0;

    const handleSubmitForm = (e) => {
        if (e) {
            e.preventDefault();
        }
        const costValue = parseFloat(cost);
        if (costValue > 0) {
            onSubmit({ cost: costValue });
            setCost('');
            setIsAdding(false);
        }
    };

    return (
        <>
            {/* Header Row (when adding) */}
            {isAdding && (
                <TableRow>
                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0, borderBottom: 'none' }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: 0, py: 1, borderBottom: 'none' }}>
                        <Typography variant="h6" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                            Add Statement
                        </Typography>
                    </TableCell>
                    <TableCell sx={{ 
                        width: '56px !important', 
                        minWidth: '56px !important', 
                        maxWidth: '56px !important', 
                        p: 0,
                        flexShrink: 0,
                        flexGrow: 0,
                        borderBottom: 'none',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%'
                        }}>
                            <IconButton
                                onClick={handleCancelAdd}
                                size="small"
                                sx={{
                                    color: 'inherit',
                                    padding: '4px',
                                    '&:hover': {
                                        backgroundColor: theme.colors.overlayHover,
                                    },
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </TableCell>
                </TableRow>
            )}
            
            {/* Form Row (when adding) */}
            {isAdding && (
                <TableRow>
                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: 0, py: 2 }}>
                        <Box
                            component="form"
                            onSubmit={handleSubmitForm}
                            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                        >
                            <TextField
                                type="number"
                                label="Cost"
                                placeholder="Enter cost amount"
                                value={cost}
                                onChange={handleInputChange}
                                onKeyDown={(e) => {
                                    // Handle Enter key to submit
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        const costValue = parseFloat(cost);
                                        if (costValue > 0) {
                                            handleSubmitForm(e);
                                        }
                                    }
                                    // Handle Escape to close
                                    if (e.key === 'Escape') {
                                        handleCancelAdd();
                                    }
                                }}
                                autoFocus
                                inputProps={{
                                    step: '0.01',
                                    min: '0',
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme.colors.text,
                                        fontSize: '16px',
                                        '& fieldset': {
                                            borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme.colors.text,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.colors.text,
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme.colors.secondary || theme.colors.brandSecondary,
                                        fontSize: '16px',
                                        '&.Mui-focused': {
                                            color: theme.colors.text,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: '16px',
                                    },
                                }}
                            />
                            
                            {!isValidCost ? (
                                <Button
                                    type="button"
                                    onClick={handleCancelAdd}
                                    variant="outlined"
                                    fullWidth
                                    sx={{
                                        border: `1px solid ${theme.colors.border || '#ccc'}`,
                                        backgroundColor: 'transparent',
                                        color: theme.colors.text,
                                        '&:hover': {
                                            borderColor: theme.colors.border || '#ccc',
                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                        },
                                    }}
                                >
                                    Cancel
                                </Button>
                            ) : (
                                <Button
                                    type="submit"
                                    variant="outlined"
                                    fullWidth
                                    sx={{
                                        border: `1px solid ${theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary}`,
                                        backgroundColor: 'transparent',
                                        color: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                        '&:hover': {
                                            borderColor: theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary,
                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                        },
                                    }}
                                >
                                    Add
                                </Button>
                            )}
                        </Box>
                    </TableCell>
                    <TableCell sx={{ 
                        width: '56px !important', 
                        minWidth: '56px !important', 
                        maxWidth: '56px !important', 
                        p: 0,
                        flexShrink: 0,
                        flexGrow: 0,
                    }}></TableCell>
                </TableRow>
            )}

            {/* Add Button Row */}
            {!isAdding && isOwner && (
                <TableRow>
                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: 0, py: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={handleStartAdd}
                            startIcon={<AddIcon />}
                            fullWidth
                            sx={{
                                borderColor: theme.current === 'light'
                                    ? theme.colors.brandSecondary
                                    : (theme.colors.secondary || theme.colors.brandSecondary),
                                color: theme.current === 'light' 
                                    ? '#000000' 
                                    : (theme.colors.secondary || theme.colors.brandSecondary),
                                backgroundColor: theme.current === 'light'
                                    ? theme.colors.brandSecondary
                                    : (theme.colors.tableBackground || theme.colors.background),
                                '&:hover': {
                                    borderColor: theme.current === 'light'
                                        ? theme.colors.brandSecondary
                                        : (theme.colors.secondary || theme.colors.brandSecondary),
                                    backgroundColor: theme.current === 'light'
                                        ? theme.colors.brandSecondary
                                        : (theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)'),
                                },
                            }}
                        >
                            Add statement
                        </Button>
                    </TableCell>
                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
                </TableRow>
            )}
        </>
    );
};

export default AddStatementRow;
